import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { GetPostsMetaParams, PostsApi } from 'app/modules/posts/api/posts';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { usePage } from 'modules/page/pageSelector';
import dayjs from 'dayjs';
import { queryKey } from 'constants/queryKey';

const getDateRange = () => ({
  dateFrom: dayjs().subtract(6, 'months').format('YYYY-MM-DD'),
  dateTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
});

const createApprovalsCountParams = (
  profileIds: number[],
): GetPostsMetaParams => ({
  ...getDateRange(),
  profiles: profileIds,
  profileGroups: [],
});

export const useApprovalsCount = () => {
  const { pages } = usePage();

  const profileIds = useMemo(() => pages.map((page) => page.id), [pages]);

  const params: GetPostsMetaParams = useMemo(
    () => createApprovalsCountParams(profileIds),
    [profileIds],
  );

  const { data, isLoading } = useQuery(
    queryKey.postsMeta(params),
    () => PostsApi.postsMeta(params),
    {
      enabled:
        (params.profiles && params.profiles.length > 0) ||
        (params.profileGroups && params.profileGroups.length > 0),
      select: (response) => ({
        internalCount:
          response.status?.[STATUS.WAITING_FOR_INTERNAL_APPROVAL] ?? 0,
        clientCount: response.status?.[STATUS.AT_CLIENT] ?? 0,
      }),
    },
  );

  const internalTotalCount = data?.internalCount ?? 0;
  const clientTotalCount = data?.clientCount ?? 0;

  const totalCount = useMemo(() => {
    return internalTotalCount + clientTotalCount;
  }, [internalTotalCount, clientTotalCount]);

  return {
    totalCount,
    internalTotalCount,
    clientTotalCount,
    isLoading,
  };
};
